import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import FormGroup from '@/core/components/FormGroup'
import { PartnerDto, PartnerUpsertDto } from '@/common/models/gamification'

import { DeleteConfirmationModal } from '../DeleteConfirmationModal'

interface CreatePartnerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  partner?: PartnerDto | null
  onSubmit: (values: PartnerUpsertDto, id?: string) => void
  onDelete?: (id: string) => void
}

interface IPartnerFormValues {
  name: string
  active: boolean
  ordinal: number
}

export const CreatePartnerModal = ({
  open,
  onOpenChange,
  partner,
  onSubmit,
  onDelete,
}: CreatePartnerModalProps) => {
  const isEditMode = !!partner
  const [deleteOpen, setDeleteOpen] = useState(false)

  const schema = yup.object().shape({
    name: yup.string().required('Name is required').max(128),
    active: yup.boolean().default(true),
    ordinal: yup.number().typeError('Order must be a number').min(0).default(0),
  })

  const defaultValues: IPartnerFormValues = {
    name: '',
    active: true,
    ordinal: 0,
  }

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, dirtyFields },
    reset,
    setValue,
  } = useForm<IPartnerFormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema) as never,
    defaultValues,
  })

  useEffect(() => {
    if (!open) return
    if (partner) {
      setValue('name', partner.name, { shouldValidate: true })
      setValue('active', partner.active, { shouldValidate: true })
      setValue('ordinal', partner.ordinal ?? 0, { shouldValidate: true })
    }
    return () => {
      reset(defaultValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, partner])

  const handleClose = () => onOpenChange(false)

  const submit = (values: IPartnerFormValues) => {
    onSubmit(
      { name: values.name, active: values.active, ordinal: values.ordinal },
      partner?.id,
    )
    handleClose()
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(o) => {
          if (!o) handleClose()
        }}
        size="md"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="heart"
              title={
                <span className="flex items-center gap-2">
                  {isEditMode ? 'Edit' : 'Add New'}{' '}
                  <span className="text-accent-1">Partner</span>
                </span>
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {isEditMode && onDelete ? (
              <Button
                onClick={() => setDeleteOpen(true)}
                variant="destructive"
                className="mr-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={isEditMode && Object.keys(dirtyFields).length === 0}
                form="create-partner-form"
                type="submit"
                variant="primary"
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="create-partner-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-6"
        >
          <FormGroup label="Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter partner name"
            />
          </FormGroup>

          <FormGroup label="Order" error={errors?.ordinal?.message}>
            <Input
              isInvalid={!!errors?.ordinal?.message}
              {...register('ordinal', { valueAsNumber: true })}
              type="number"
              placeholder="0"
            />
          </FormGroup>

          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(!!checked)}
                />
                <span className="text-body-medium text-monochrome-1">
                  Active
                </span>
              </label>
            )}
          />
        </form>
      </Modal>

      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteOpen}
        setDeleteConfirmationModal={setDeleteOpen}
        deletedItem={partner?.name}
        deleteFunc={() => {
          if (partner && onDelete) onDelete(partner.id)
          handleClose()
        }}
      />
    </>
  )
}
